/**
 * Wunschplan KS — abstrahierter Kampagnen-Wunschplan (Soll)
 *
 * Spec: docs/superpowers/specs/2026-06-05-ks-wunschplan-design.md
 *
 * Klaus Strottmann plant Kampagnen aus einer abstrakten Steuerungssicht:
 * "Lose gesamt" -> (Wandlungs-Calls -> Calls -> Adressen -> Kosten). Lose werden
 * anhand einer FESTEN Callcenter-Kapazitaet (Calls/Woche) automatisch ueber KWs
 * verteilt; manuelle KW-Edits reflowen serverseitig (Summe bleibt konstant).
 *
 * DUMMY/SEED: Quoten-Defaults kommen aus der geseedeten Cache-Tabelle
 * (ks_dialfire_quote_cache), manuell ueberschreibbar. Kein Live-Dialfire.
 *
 * ADDITIV: KsPlanung.tsx und Einsatzplanung.tsx bleiben unangetastet. Diese
 * Seite nutzt KEINE Imports aus jenen Dateien, kein MUI — nur Inline-Styles.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { ChartBar } from '@phosphor-icons/react';
import { getIngestUrl, authFetch } from '../config';
import SubPageHeader from '../components/SubPageHeader';

// ===== Typen =====

interface Plan {
  id: number;
  name: string;
  jahr: number;
  kapazitaet_calls_pro_woche: number;
  preis_eigen: number | string;
  preis_fremd: number | string;
  preis_cc_lead: number | string | null;
  status: string;
}

interface Kampagne {
  id: number;
  plan_id: number;
  name: string;
  lottery_id: number | null;
  lose_gesamt: number;
  start_kw: number;
  start_jahr: number;
  eigen_anteil_pct: number | string;
  sort_order: number;
}

interface KwRow {
  kw_nummer: number;
  jahr: number;
  lose: number;
  manuell_geaendert: number;
}

interface SeitenErgebnis {
  lose: number;
  wandlungsCalls: number;
  calls: number;
  adressen: number;
}

interface CalcResult {
  eigen: SeitenErgebnis;
  fremd: SeitenErgebnis;
  gesamt: SeitenErgebnis;
  kosten: number;
}

interface QuoteSeite {
  response_pct: number | null;
  wandlung_pct: number | null;
  lose_pro_call: number | null;
  herkunft: { response: string; wandlung: string; lose_pro_call: string };
  stand: string | null;
}

interface QuoteRow {
  lottery_id: number | null;
  eigen: QuoteSeite;
  fremd: QuoteSeite;
}

interface KampagneDetail {
  kw: KwRow[];
  calc: CalcResult;
  losePerWeek: number;
  quoten: { eigen: QuoteSeite; fremd: QuoteSeite };
}

// ===== Styles (Inline, gemeinsam) =====

const card: React.CSSProperties = {
  backgroundColor: '#ffffff',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  padding: '1rem',
  marginBottom: '1rem',
};
const sectionTitle: React.CSSProperties = {
  margin: '0 0 0.75rem 0',
  fontSize: '1.05rem',
  color: '#1e3a8a',
};
const label: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8rem',
  color: '#475569',
  marginBottom: '0.2rem',
};
const input: React.CSSProperties = {
  padding: '0.4rem 0.5rem',
  border: '1px solid #cbd5e1',
  borderRadius: '4px',
  fontSize: '0.9rem',
  width: '100%',
  boxSizing: 'border-box',
};
const btn: React.CSSProperties = {
  padding: '0.45rem 0.9rem',
  backgroundColor: '#1e40af',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '0.9rem',
};
const btnGhost: React.CSSProperties = {
  ...btn,
  backgroundColor: '#e2e8f0',
  color: '#1e293b',
};
const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.35rem 0.5rem',
  borderBottom: '2px solid #cbd5e1',
  fontSize: '0.8rem',
  color: '#475569',
};
const td: React.CSSProperties = {
  padding: '0.3rem 0.5rem',
  borderBottom: '1px solid #eef2f7',
  fontSize: '0.85rem',
};

const fmt = (n: number | null | undefined, digits = 0) =>
  (n == null || !isFinite(Number(n)))
    ? '-'
    : Number(n).toLocaleString('de-DE', { maximumFractionDigits: digits, minimumFractionDigits: 0 });

const eur = (n: number | null | undefined) =>
  (n == null || !isFinite(Number(n)))
    ? '-'
    : Number(n).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

// ===== Komponente =====

export default function KsWunschplan() {
  const [plaene, setPlaene] = useState<Plan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [kampagnen, setKampagnen] = useState<Kampagne[]>([]);
  const [details, setDetails] = useState<Record<number, KampagneDetail>>({});
  const [quoten, setQuoten] = useState<QuoteRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Neuer-Plan-Dialog
  const [showNewPlan, setShowNewPlan] = useState(false);
  const [newPlanName, setNewPlanName] = useState('');
  const [newPlanJahr, setNewPlanJahr] = useState(new Date().getFullYear());

  // Konfig-Formularzustand (Kapazitaet/Preise)
  const [cfgKap, setCfgKap] = useState('');
  const [cfgPe, setCfgPe] = useState('');
  const [cfgPf, setCfgPf] = useState('');
  const [cfgCc, setCfgCc] = useState('');

  // Neue Kampagne
  const [nkName, setNkName] = useState('');
  const [nkLottery, setNkLottery] = useState('');
  const [nkLose, setNkLose] = useState('');
  const [nkKw, setNkKw] = useState('1');
  const [nkJahr, setNkJahr] = useState(String(new Date().getFullYear()));
  const [nkEigen, setNkEigen] = useState('100');

  // Live-Mix pro Kampagne (clientseitige Vorschau)
  const [mix, setMix] = useState<Record<number, number>>({});

  const loadPlaene = useCallback(async () => {
    try {
      const res = await authFetch(getIngestUrl('/ks-wunsch/plaene'));
      const json = await res.json();
      if (json.success) {
        setPlaene(json.data);
        if (json.data.length > 0 && selectedPlanId == null) {
          setSelectedPlanId(json.data[0].id);
        }
      }
    } catch (e) {
      setError('Plaene konnten nicht geladen werden');
    }
  }, [selectedPlanId]);

  const loadDetail = useCallback(async (kampagneId: number) => {
    const res = await authFetch(getIngestUrl(`/ks-wunsch/kampagnen/${kampagneId}/kw`));
    const json = await res.json();
    if (json.success) {
      setDetails((prev) => ({ ...prev, [kampagneId]: json.data }));
    }
  }, []);

  const loadPlanData = useCallback(async (planId: number) => {
    setLoading(true);
    setError(null);
    try {
      const pRes = await authFetch(getIngestUrl(`/ks-wunsch/plaene/${planId}`));
      const pJson = await pRes.json();
      if (pJson.success) {
        setPlan(pJson.data);
        setCfgKap(String(pJson.data.kapazitaet_calls_pro_woche ?? ''));
        setCfgPe(String(pJson.data.preis_eigen ?? ''));
        setCfgPf(String(pJson.data.preis_fremd ?? ''));
        setCfgCc(pJson.data.preis_cc_lead == null ? '' : String(pJson.data.preis_cc_lead));
      }

      const kRes = await authFetch(getIngestUrl(`/ks-wunsch/plaene/${planId}/kampagnen`));
      const kJson = await kRes.json();
      const kamps: Kampagne[] = kJson.success ? kJson.data : [];
      setKampagnen(kamps);

      const initMix: Record<number, number> = {};
      kamps.forEach((k) => { initMix[k.id] = Number(k.eigen_anteil_pct) || 0; });
      setMix(initMix);

      setDetails({});
      for (const k of kamps) {
        await loadDetail(k.id);
      }

      const qRes = await authFetch(getIngestUrl(`/ks-wunsch/plaene/${planId}/quoten`));
      const qJson = await qRes.json();
      setQuoten(qJson.success ? qJson.data : []);
    } catch (e) {
      setError('Plan-Daten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [loadDetail]);

  useEffect(() => { loadPlaene(); }, [loadPlaene]);
  useEffect(() => { if (selectedPlanId != null) loadPlanData(selectedPlanId); }, [selectedPlanId, loadPlanData]);

  // ----- Aktionen -----

  const createPlan = async () => {
    if (!newPlanName.trim()) return;
    const res = await authFetch(getIngestUrl('/ks-wunsch/plaene'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: newPlanName.trim(), jahr: newPlanJahr }),
    });
    const json = await res.json();
    if (json.success) {
      setShowNewPlan(false);
      setNewPlanName('');
      await loadPlaene();
      setSelectedPlanId(json.data.id);
    }
  };

  const saveKonfig = async () => {
    if (selectedPlanId == null) return;
    await authFetch(getIngestUrl(`/ks-wunsch/plaene/${selectedPlanId}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        kapazitaet_calls_pro_woche: cfgKap === '' ? 0 : Number(cfgKap),
        preis_eigen: cfgPe === '' ? 0 : Number(cfgPe),
        preis_fremd: cfgPf === '' ? 0 : Number(cfgPf),
        preis_cc_lead: cfgCc === '' ? '' : Number(cfgCc),
      }),
    });
    await loadPlanData(selectedPlanId);
  };

  const addKampagne = async () => {
    if (selectedPlanId == null || !nkName.trim()) return;
    await authFetch(getIngestUrl(`/ks-wunsch/plaene/${selectedPlanId}/kampagnen`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: nkName.trim(),
        lottery_id: nkLottery,
        lose_gesamt: nkLose,
        start_kw: nkKw,
        start_jahr: nkJahr,
        eigen_anteil_pct: nkEigen,
        sort_order: kampagnen.length + 1,
      }),
    });
    setNkName(''); setNkLottery(''); setNkLose('');
    await loadPlanData(selectedPlanId);
  };

  const deleteKampagne = async (id: number) => {
    await authFetch(getIngestUrl(`/ks-wunsch/kampagnen/${id}`), { method: 'DELETE' });
    if (selectedPlanId != null) await loadPlanData(selectedPlanId);
  };

  const editKw = async (kampagneId: number, row: KwRow, neuerWert: number) => {
    await authFetch(getIngestUrl(`/ks-wunsch/kampagnen/${kampagneId}/kw/${row.kw_nummer}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ jahr: row.jahr, lose: neuerWert }),
    });
    await loadDetail(kampagneId);
  };

  const commitMix = async (kampagneId: number, value: number) => {
    await authFetch(getIngestUrl(`/ks-wunsch/kampagnen/${kampagneId}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ eigen_anteil_pct: value }),
    });
    if (selectedPlanId != null) await loadPlanData(selectedPlanId);
  };

  const saveQuote = async (
    lotteryId: number | null,
    kategorie: 'eigen' | 'fremd',
    feld: 'response_pct' | 'wandlung_pct' | 'lose_pro_call',
    wert: string
  ) => {
    if (selectedPlanId == null) return;
    const row = quoten.find((q) => q.lottery_id === lotteryId);
    const seite = row ? row[kategorie] : null;
    const body: any = {
      lottery_id: lotteryId,
      kategorie,
      response_pct: seite?.response_pct ?? '',
      wandlung_pct: seite?.wandlung_pct ?? '',
      lose_pro_call: seite?.lose_pro_call ?? '',
    };
    body[feld] = wert === '' ? '' : Number(wert);
    await authFetch(getIngestUrl(`/ks-wunsch/plaene/${selectedPlanId}/quoten`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    await loadPlanData(selectedPlanId);
  };

  // ----- Live-Mix-Vorschau (clientseitige Spiegelung von computeKampagne) -----

  const livePreview = (k: Kampagne): CalcResult | null => {
    const detail = details[k.id];
    if (!detail) return null;
    const anteil = Math.min(100, Math.max(0, mix[k.id] ?? Number(k.eigen_anteil_pct))) / 100;
    const q = detail.quoten;
    const sd = (z: number, n: number) => (!n || !isFinite(n) ? 0 : (isFinite(z / n) ? z / n : 0));
    const seite = (lose: number, s: QuoteSeite): SeitenErgebnis => {
      const wc = sd(lose, Number(s.lose_pro_call) || 0);
      const calls = sd(wc, (Number(s.wandlung_pct) || 0) / 100);
      const adr = sd(calls, (Number(s.response_pct) || 0) / 100);
      return { lose, wandlungsCalls: wc, calls, adressen: adr };
    };
    const loseE = Number(k.lose_gesamt) * anteil;
    const loseF = Number(k.lose_gesamt) - loseE;
    const eigen = seite(loseE, q.eigen);
    const fremd = seite(loseF, q.fremd);
    const gesamt: SeitenErgebnis = {
      lose: eigen.lose + fremd.lose,
      wandlungsCalls: eigen.wandlungsCalls + fremd.wandlungsCalls,
      calls: eigen.calls + fremd.calls,
      adressen: eigen.adressen + fremd.adressen,
    };
    const pe = Number(plan?.preis_eigen) || 0;
    const pf = Number(plan?.preis_fremd) || 0;
    const cc = plan?.preis_cc_lead != null && plan.preis_cc_lead !== '' ? Number(plan.preis_cc_lead) : null;
    let kosten = eigen.adressen * pe + fremd.adressen * pf;
    if (cc != null) kosten += gesamt.calls * cc;
    return { eigen, fremd, gesamt, kosten };
  };

  // ----- Summen -----

  const summen = kampagnen.reduce(
    (acc, k) => {
      const c = livePreview(k) || details[k.id]?.calc;
      if (c) {
        acc.lose += c.gesamt.lose;
        acc.adressen += c.gesamt.adressen;
        acc.kosten += c.kosten;
      }
      return acc;
    },
    { lose: 0, adressen: 0, kosten: 0 }
  );

  // ===== Render =====

  return (
    <div>
      <SubPageHeader title="Wunschplan KS" icon={<ChartBar size={22} weight="fill" />} />
      <div style={{ padding: '1.5rem', maxWidth: '1200px', margin: '0 auto' }}>

        {error && (
          <div style={{ ...card, backgroundColor: '#fef2f2', borderColor: '#fecaca', color: '#b91c1c' }}>
            {error}
          </div>
        )}

        {/* Plan-Auswahl */}
        <div style={{ ...card, display: 'flex', alignItems: 'flex-end', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ minWidth: '240px' }}>
            <label style={label}>Wunschplan</label>
            <select
              style={input}
              value={selectedPlanId ?? ''}
              onChange={(e) => setSelectedPlanId(e.target.value ? Number(e.target.value) : null)}
            >
              {plaene.length === 0 && <option value="">— kein Plan —</option>}
              {plaene.map((p) => (
                <option key={p.id} value={p.id}>{p.name} ({p.jahr})</option>
              ))}
            </select>
          </div>
          <button style={btnGhost} onClick={() => setShowNewPlan((v) => !v)}>+ Neuer Plan</button>
        </div>

        {showNewPlan && (
          <div style={card}>
            <h3 style={sectionTitle}>Neuer Plan</h3>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <label style={label}>Name</label>
                <input style={input} value={newPlanName} onChange={(e) => setNewPlanName(e.target.value)} />
              </div>
              <div style={{ width: '120px' }}>
                <label style={label}>Jahr</label>
                <input style={input} type="number" value={newPlanJahr}
                  onChange={(e) => setNewPlanJahr(Number(e.target.value))} />
              </div>
              <button style={btn} onClick={createPlan}>Anlegen</button>
            </div>
          </div>
        )}

        {loading && <div style={card}>Lade…</div>}

        {plan && (
          <>
            {/* Konfig: Kapazitaet + Preise */}
            <div style={card}>
              <h3 style={sectionTitle}>Kapazität & Preise</h3>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div style={{ width: '180px' }}>
                  <label style={label}>Calls / Woche (Kapazität)</label>
                  <input style={input} type="number" value={cfgKap} onChange={(e) => setCfgKap(e.target.value)} />
                </div>
                <div style={{ width: '140px' }}>
                  <label style={label}>€ / Adresse eigen</label>
                  <input style={input} type="number" step="0.0001" value={cfgPe} onChange={(e) => setCfgPe(e.target.value)} />
                </div>
                <div style={{ width: '140px' }}>
                  <label style={label}>€ / Adresse fremd</label>
                  <input style={input} type="number" step="0.0001" value={cfgPf} onChange={(e) => setCfgPf(e.target.value)} />
                </div>
                <div style={{ width: '160px' }}>
                  <label style={label}>CC-Kosten / Lead (optional)</label>
                  <input style={input} type="number" step="0.0001" value={cfgCc} onChange={(e) => setCfgCc(e.target.value)} />
                </div>
                <button style={btn} onClick={saveKonfig}>Speichern</button>
              </div>
            </div>

            {/* Konfig: Quoten */}
            <div style={card}>
              <h3 style={sectionTitle}>Quoten (Default aus Daten, überschreibbar)</h3>
              {quoten.length === 0 && <p style={{ color: '#64748b', fontSize: '0.85rem' }}>Keine Kampagnen — keine Lotterien.</p>}
              {quoten.map((q) => (
                <div key={String(q.lottery_id)} style={{ marginBottom: '1rem' }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                    Lotterie {q.lottery_id ?? '—'}
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={th}>Kategorie</th>
                        <th style={th}>Response %</th>
                        <th style={th}>Wandlung %</th>
                        <th style={th}>Lose / Call</th>
                        <th style={th}>Herkunft</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(['eigen', 'fremd'] as const).map((kat) => {
                        const s = q[kat];
                        return (
                          <tr key={kat}>
                            <td style={td}>{kat}</td>
                            <td style={td}>
                              <input style={{ ...input, width: '90px' }} type="number" step="0.001"
                                defaultValue={s.response_pct ?? ''}
                                onBlur={(e) => saveQuote(q.lottery_id, kat, 'response_pct', e.target.value)} />
                            </td>
                            <td style={td}>
                              <input style={{ ...input, width: '90px' }} type="number" step="0.001"
                                defaultValue={s.wandlung_pct ?? ''}
                                onBlur={(e) => saveQuote(q.lottery_id, kat, 'wandlung_pct', e.target.value)} />
                            </td>
                            <td style={td}>
                              <input style={{ ...input, width: '90px' }} type="number" step="0.00001"
                                defaultValue={s.lose_pro_call ?? ''}
                                onBlur={(e) => saveQuote(q.lottery_id, kat, 'lose_pro_call', e.target.value)} />
                            </td>
                            <td style={{ ...td, fontSize: '0.75rem', color: '#64748b' }}>
                              {s.herkunft.response === 'override'
                                ? 'Override'
                                : `Wert aus Daten${s.stand ? ` (Stand ${s.stand})` : ''}`}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>

            {/* Neue Kampagne */}
            <div style={card}>
              <h3 style={sectionTitle}>Kampagne anlegen</h3>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div style={{ flex: 1, minWidth: '160px' }}>
                  <label style={label}>Name</label>
                  <input style={input} value={nkName} onChange={(e) => setNkName(e.target.value)} />
                </div>
                <div style={{ width: '110px' }}>
                  <label style={label}>Lotterie-ID</label>
                  <input style={input} type="number" value={nkLottery} onChange={(e) => setNkLottery(e.target.value)} />
                </div>
                <div style={{ width: '120px' }}>
                  <label style={label}>Lose gesamt</label>
                  <input style={input} type="number" value={nkLose} onChange={(e) => setNkLose(e.target.value)} />
                </div>
                <div style={{ width: '90px' }}>
                  <label style={label}>Start-KW</label>
                  <input style={input} type="number" value={nkKw} onChange={(e) => setNkKw(e.target.value)} />
                </div>
                <div style={{ width: '90px' }}>
                  <label style={label}>Start-Jahr</label>
                  <input style={input} type="number" value={nkJahr} onChange={(e) => setNkJahr(e.target.value)} />
                </div>
                <div style={{ width: '100px' }}>
                  <label style={label}>Eigen %</label>
                  <input style={input} type="number" value={nkEigen} onChange={(e) => setNkEigen(e.target.value)} />
                </div>
                <button style={btn} onClick={addKampagne}>Anlegen</button>
              </div>
            </div>

            {/* Kampagnen */}
            {kampagnen.map((k) => {
              const detail = details[k.id];
              const calc = livePreview(k) || detail?.calc;
              const mixVal = mix[k.id] ?? Number(k.eigen_anteil_pct);
              return (
                <div key={k.id} style={card}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={sectionTitle}>
                      {k.name} <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                        (Lotterie {k.lottery_id ?? '—'}, {fmt(Number(k.lose_gesamt))} Lose, ab KW{k.start_kw}/{k.start_jahr})
                      </span>
                    </h3>
                    <button style={{ ...btnGhost, color: '#b91c1c' }} onClick={() => deleteKampagne(k.id)}>Löschen</button>
                  </div>

                  {/* Mix-Slider */}
                  <div style={{ margin: '0.5rem 0 1rem 0' }}>
                    <label style={label}>Eigen/Fremd-Mix: {fmt(mixVal)} % eigen / {fmt(100 - mixVal)} % fremd</label>
                    <input
                      type="range" min={0} max={100} value={mixVal}
                      style={{ width: '320px' }}
                      onChange={(e) => setMix((prev) => ({ ...prev, [k.id]: Number(e.target.value) }))}
                      onMouseUp={(e) => commitMix(k.id, Number((e.target as HTMLInputElement).value))}
                      onTouchEnd={(e) => commitMix(k.id, Number((e.target as HTMLInputElement).value))}
                    />
                  </div>

                  {/* 3-Stufen-Calc */}
                  {calc && (
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '1rem' }}>
                      <thead>
                        <tr>
                          <th style={th}>Stufe</th>
                          <th style={th}>Eigen</th>
                          <th style={th}>Fremd</th>
                          <th style={th}>Gesamt</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={td}>Lose</td>
                          <td style={td}>{fmt(calc.eigen.lose)}</td>
                          <td style={td}>{fmt(calc.fremd.lose)}</td>
                          <td style={td}>{fmt(calc.gesamt.lose)}</td>
                        </tr>
                        <tr>
                          <td style={td}>Wandlungs-Calls</td>
                          <td style={td}>{fmt(calc.eigen.wandlungsCalls)}</td>
                          <td style={td}>{fmt(calc.fremd.wandlungsCalls)}</td>
                          <td style={td}>{fmt(calc.gesamt.wandlungsCalls)}</td>
                        </tr>
                        <tr>
                          <td style={td}>Calls gesamt</td>
                          <td style={td}>{fmt(calc.eigen.calls)}</td>
                          <td style={td}>{fmt(calc.fremd.calls)}</td>
                          <td style={td}>{fmt(calc.gesamt.calls)}</td>
                        </tr>
                        <tr>
                          <td style={td}>Adressen</td>
                          <td style={td}>{fmt(calc.eigen.adressen)}</td>
                          <td style={td}>{fmt(calc.fremd.adressen)}</td>
                          <td style={td}>{fmt(calc.gesamt.adressen)}</td>
                        </tr>
                        <tr>
                          <td style={{ ...td, fontWeight: 600 }}>Kosten</td>
                          <td style={td}></td>
                          <td style={td}></td>
                          <td style={{ ...td, fontWeight: 600 }}>{eur(calc.kosten)}</td>
                        </tr>
                      </tbody>
                    </table>
                  )}

                  {/* KW-Tabelle (editierbar) */}
                  {detail && (
                    <div>
                      <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '0.3rem' }}>
                        Lose / Woche (Kapazität): {fmt(detail.losePerWeek)}
                      </div>
                      <table style={{ borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            <th style={th}>KW</th>
                            <th style={th}>Jahr</th>
                            <th style={th}>Lose</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.kw.map((row) => (
                            <tr key={`${row.jahr}-${row.kw_nummer}`}
                              style={{ backgroundColor: row.manuell_geaendert ? '#fef9c3' : undefined }}>
                              <td style={td}>KW{row.kw_nummer}</td>
                              <td style={td}>{row.jahr}</td>
                              <td style={td}>
                                <input
                                  style={{ ...input, width: '100px' }}
                                  type="number"
                                  defaultValue={row.lose}
                                  onBlur={(e) => {
                                    const v = Number(e.target.value);
                                    if (v !== row.lose) editKw(k.id, row, v);
                                  }}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Summenzeile */}
            {kampagnen.length > 0 && (
              <div style={{ ...card, backgroundColor: '#eff6ff', borderColor: '#bfdbfe' }}>
                <h3 style={sectionTitle}>Summe über alle Kampagnen</h3>
                <table style={{ borderCollapse: 'collapse' }}>
                  <tbody>
                    <tr>
                      <td style={{ ...td, fontWeight: 600, paddingRight: '2rem' }}>Lose</td>
                      <td style={td}>{fmt(summen.lose)}</td>
                    </tr>
                    <tr>
                      <td style={{ ...td, fontWeight: 600, paddingRight: '2rem' }}>Adressen</td>
                      <td style={td}>{fmt(summen.adressen)}</td>
                    </tr>
                    <tr>
                      <td style={{ ...td, fontWeight: 600, paddingRight: '2rem' }}>Kosten</td>
                      <td style={{ ...td, fontWeight: 600 }}>{eur(summen.kosten)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
